import { cva } from "class-variance-authority";
import type { ReactNode } from "react";
import { Column, Container, Row } from "../../layout/grid";
import { cn } from "../../lib/utils";
import type { ImageArrangementProps } from "./image-arrangement-types";
import { RowImageArrangement } from "./row-image-arrangement";
import { SimpleColumnsImageArrangement } from "./simple-columns-image-arrangement";
import { StaggeredImageArrangement } from "./staggered-image-arrangement";

const imageArrangementVariants = cva("", {
  variants: {
    background: {
      white: "bg-white",
      whitesmoke: "bg-whitesmoke",
      black: "bg-black",
    },
  },
  defaultVariants: {
    background: "white",
  },
});

export function ImageArrangement(
  props: Readonly<ImageArrangementProps>
): ReactNode {
  if (!props.images.length) {
    return null;
  }

  const STATIC_LAYOUT_THRESHOLD = 3;
  const fullWidth = props.fullWidth ?? true;
  const variant = props.variant;
  const background = props.background ?? "white";
  const bgClasses = imageArrangementVariants({ background });

  let content: ReactNode;

  if (variant === "staggered") {
    content = (
      <StaggeredImageArrangement
        background={background}
        images={props.images}
        readMoreLabel={props.readMoreLabel}
        readMoreModalTitle={props.readMoreModalTitle}
      />
    );
  } else if (variant === "simple-columns") {
    content = (
      <SimpleColumnsImageArrangement
        background={background}
        images={props.images}
        readMoreLabel={props.readMoreLabel}
        readMoreModalTitle={props.readMoreModalTitle}
      />
    );
  } else {
    content = (
      <RowImageArrangement
        background={background}
        fullWidth={fullWidth}
        headline={props.headline}
        headlineInspectorProps={props.inspectorProps?.headline}
        hideCarouselNavigationArrows
        images={props.images}
        nextSlideText={props.nextSlideText}
        previousSlideText={props.previousSlideText}
        readMoreLabel={props.readMoreLabel}
        readMoreModalTitle={props.readMoreModalTitle}
        staticLayoutThreshold={STATIC_LAYOUT_THRESHOLD}
        title={props.title}
      />
    );
  }

  const isStaticLayout =
    variant === "row" && props.images.length <= STATIC_LAYOUT_THRESHOLD;

  let contentColumnClassName: string;
  if (fullWidth && variant === "row" && !isStaticLayout) {
    contentColumnClassName = "col-span-12 px-0 md:pl-8";
  } else if (fullWidth || variant === "staggered") {
    contentColumnClassName = "col-span-12";
  } else {
    contentColumnClassName = "col-span-12 md:col-span-10 md:col-start-2";
  }

  return (
    <Container
      className={cn("component-image-arrangement", bgClasses, props.className)}
      fullWidth={true}
      outerContainerOnly={fullWidth}
      paddingBottom={props.paddingBottom}
      paddingTop={props.paddingTop}
      testId="image-arrangement"
      {...props.inspectorProps?.container}
    >
      <Row className="gap-0">
        <Column className={contentColumnClassName}>{content}</Column>
      </Row>
    </Container>
  );
}

export type {
  ImageArrangementItem,
  ImageArrangementProps,
} from "./image-arrangement-types";
